<template>
  <a-modal
      :visible="visible"
      :title="`分配用户${role.name ? ' - ' + role.name : ''}`"
      width="70%"
      :footer="null"
      :maskClosable="false"
      @cancel="handleCancel"
  >
    <!-- 搜索 + 添加用户 + 批量删除 -->
    <div class="search-bar">
      <a-input v-model="searchForm.username" placeholder="用户名" allow-clear style="width: 180px" @keyup.enter.native="searchUsers"/>
      <a-input v-model="searchForm.mobile" placeholder="手机号" allow-clear style="width: 180px; margin-left: 8px" @keyup.enter.native="searchUsers"/>
      <a-space style="margin-left: 8px">
        <a-button type="primary" @click="searchUsers">查询</a-button>
        <a-button @click="resetSearch">重置</a-button>
      </a-space>
      <EmployeeSelector
          :single="false"
          title="选择用户"
          :disabled="bindLoading"
          @change="onUserSelect"
      >
        <a-button type="primary" icon="plus" :loading="bindLoading" style="margin-left: 8px">
          添加用户
        </a-button>
      </EmployeeSelector>
      <a-button
          icon="delete"
          :disabled="selectedRowKeys.length === 0"
          :loading="batchLoading"
          @click="batchRemoveUsers"
          :style="{ marginLeft: '8px', color: selectedRowKeys.length > 0 ? '#f5222d' : '' }"
      >
        批量删除{{ selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "" }}
      </a-button>
    </div>

    <!-- 已分配用户列表（外层滚动） -->
    <div class="table-scroll">
      <a-table
          bordered
          size="small"
          :dataSource="pagedList"
          :loading="userLoading"
          :pagination="false"
          :rowKey="(record) => record.uuid"
          :row-selection="{
            selectedRowKeys: selectedRowKeys,
            onChange: onSelectRowChange,
          }"
      >
        <a-table-column key="index" title="序号" align="center" :width="60">
          <template slot-scope="text, record, index">{{ (userCurrent - 1) * userPageSize + index + 1 }}</template>
        </a-table-column>
        <a-table-column key="username" title="用户名" data-index="username"/>
        <a-table-column key="mobile" title="手机号" data-index="mobile"/>
        <a-table-column key="deptName" title="部门" data-index="deptName"/>
        <a-table-column key="status" title="状态" data-index="status" align="center">
          <template slot-scope="status">
            <a-badge v-if="status == 1" status="success" text="正常"/>
            <a-badge v-else status="error" text="停用"/>
          </template>
        </a-table-column>
        <a-table-column
            key="op"
            title="操作"
            align="center"
            :width="100"
        >
          <template slot-scope="text">
            <a-button
                type="link"
                size="small"
                style="color: #f5222d"
                :loading="removingUuid === text.uuid"
                @click="removeUserFromRole(text)"
            >
              删除
            </a-button>
          </template>
        </a-table-column>
      </a-table>
    </div>

    <!-- 分页（在滚动区外，固定在底部） -->
    <a-pagination
        :current="userCurrent"
        :pageSize="userPageSize"
        :total="filteredUserList.length"
        showSizeChanger
        showQuickJumper
        :pageSizeOptions="['10', '20', '50', '100']"
        :showTotal="(total) => `共 ${total} 条记录`"
        @change="onPageChange"
        @showSizeChange="onShowSizeChange"
        style="margin-top: 12px; text-align: right"
    />
  </a-modal>
</template>

<script>
import {fetchDetail, updateUsers} from "@/api/setting/role";
import EmployeeSelector from "@/components/employee-selector";

export default {
  name: "roleUserModal",
  components: {EmployeeSelector},
  props: {
    // 弹窗显隐，配合 .sync 使用
    visible: {type: Boolean, default: false},
    // 当前操作的角色（列表行，含 uuid/name）
    role: {type: Object, default: () => ({})},
  },
  data() {
    return {
      roleUserList: [], // 该角色下已分配的用户
      searchForm: {username: "", mobile: ""}, // 搜索输入
      appliedFilter: {username: "", mobile: ""}, // 已应用的过滤条件（点查询后生效）
      userLoading: false,
      removingUuid: "",
      bindLoading: false,
      selectedRowKeys: [], // 批量删除选中的用户 uuid
      batchLoading: false,
      userCurrent: 1, // 当前页
      userPageSize: 10, // 每页条数
    };
  },
  computed: {
    // 按已应用的过滤条件本地过滤已分配用户
    filteredUserList() {
      const u = (this.appliedFilter.username || "").trim();
      const m = (this.appliedFilter.mobile || "").trim();
      if (!u && !m) {
        return this.roleUserList;
      }
      return this.roleUserList.filter((item) => {
        const matchName = !u || (item.username && item.username.indexOf(u) > -1);
        const matchMobile = !m || (item.mobile && item.mobile.indexOf(m) > -1);
        return matchName && matchMobile;
      });
    },
    // 当前页切片数据（外部分页，表格只展示当前页）
    pagedList() {
      const start = (this.userCurrent - 1) * this.userPageSize;
      return this.filteredUserList.slice(start, start + this.userPageSize);
    },
  },
  watch: {
    visible(val) {
      // 弹窗打开时加载已分配用户
      if (val && this.role.uuid) {
        this.removingUuid = "";
        this.userCurrent = 1;
        this.getRoleUsers();
      }
      // 关闭时清空搜索条件和选中
      if (!val) {
        this.searchForm = {username: "", mobile: ""};
        this.appliedFilter = {username: "", mobile: ""};
        this.selectedRowKeys = [];
        this.userCurrent = 1;
      }
    },
  },
  methods: {
    handleCancel() {
      this.$emit("update:visible", false);
    },

    // 查询：把输入条件应用到过滤
    searchUsers() {
      this.appliedFilter = {...this.searchForm};
      this.userCurrent = 1;
    },
    // 重置：清空输入和已应用条件
    resetSearch() {
      this.searchForm = {username: "", mobile: ""};
      this.appliedFilter = {username: "", mobile: ""};
      this.userCurrent = 1;
    },
    // 翻页
    onPageChange(page) {
      this.userCurrent = page;
    },
    // 切换每页条数（showSizeChanger 改变时触发，change 不会触发）
    onShowSizeChange(current, size) {
      this.userCurrent = 1;
      this.userPageSize = size;
    },

    // 表格多选变化
    onSelectRowChange(keys) {
      this.selectedRowKeys = keys;
    },

    // 查询角色详情（含已分配用户）
    // 接口：/user-service/role/id?uuid=xxx
    // 假设返回 res.users 为已分配用户数组；若后端字段名不是 users，
    // 只需把下面 res.users 改成对应字段名即可。
    getRoleUsers() {
      this.userLoading = true;
      fetchDetail({
        uuid: this.role.uuid,
      }).then((res) => {
        this.userLoading = false;
        this.roleUserList = Array.isArray(res.users) ? res.users : [];
      }).catch(() => {
        this.userLoading = false;
        this.roleUserList = [];
      });
    },

    // 用户选择器确认回调：取员工的 userId 作为用户 uuid，合并已有后调用 updateUsers
    // （employee 对象含 userId 字段关联用户主键，见 sign.vue 用法）
    onUserSelect(arr) {
      if (!Array.isArray(arr) || arr.length === 0) {
        return;
      }
      const newUserUuids = arr.map((e) => e.userId).filter(Boolean);
      if (newUserUuids.length === 0) {
        this.$message.warning("所选员工未关联用户账号，无法添加");
        return;
      }
      this.bindLoading = true;
      const existingUuids = this.roleUserList.map((u) => u.uuid);
      const merged = [...new Set([...existingUuids, ...newUserUuids])];
      updateUsers({
        roleUUId: this.role.uuid,
        userUUIds: merged,
      }).then(() => {
        this.bindLoading = false;
        this.getRoleUsers();
      }).catch(() => {
        this.bindLoading = false;
      });
    },

    // 从当前角色删除某个用户：剔除后调用 updateUsers 提交剩余用户列表
    removeUserFromRole(text) {
      const that = this;
      this.$confirm({
        title: "确认要将该用户从当前角色中删除吗？",
        onOk() {
          that.removingUuid = text.uuid;
          const remaining = that.roleUserList
              .filter((u) => u.uuid !== text.uuid)
              .map((u) => u.uuid);
          updateUsers({
            roleUUId: that.role.uuid,
            userUUIds: remaining,
          }).then(() => {
            that.removingUuid = "";
            that.selectedRowKeys = [];
            that.getRoleUsers();
          }).catch(() => {
            that.removingUuid = "";
          });
        },
      });
    },

    // 批量删除：把选中的用户从角色中剔除，调用 updateUsers 提交剩余用户列表
    batchRemoveUsers() {
      if (this.selectedRowKeys.length === 0) {
        this.$message.warning("请先勾选要删除的用户");
        return;
      }
      const that = this;
      this.$confirm({
        title: `确认要删除选中的 ${this.selectedRowKeys.length} 个用户吗？`,
        onOk() {
          that.batchLoading = true;
          const set = new Set(that.selectedRowKeys);
          const remaining = that.roleUserList
              .filter((u) => !set.has(u.uuid))
              .map((u) => u.uuid);
          updateUsers({
            roleUUId: that.role.uuid,
            userUUIds: remaining,
          }).then(() => {
            that.batchLoading = false;
            that.selectedRowKeys = [];
            that.getRoleUsers();
          }).catch(() => {
            that.batchLoading = false;
          });
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.search-bar {
  display: flex;
  align-items: center;
}

.table-scroll {
  max-height: 360px;
  overflow-y: auto;
  margin-top: 12px;
}
</style>
